/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { gql, type TypedDocumentNode } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogMedia,
  AlertDialogTitle,
  Button,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Spinner,
  Textarea,
} from '@repo/shared-client/components/ui'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from '@web/src/lib/intl/react'
import { CheckIcon, CopyIcon, HeartHandshakeIcon, Trash2Icon } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import type { DeleteProfileMutation, DeleteProfileMutationVariables, DeletionReasonEnum } from 'types/graphql'
import { useAuth } from '../../../hooks/use-auth'

export const DELETE_PROFILE: TypedDocumentNode<DeleteProfileMutation, DeleteProfileMutationVariables> = gql`
  mutation DeleteProfile($id: String!, $deletionInput: DeleteProfileInput!) {
    deleteProfile(id: $id, deletionInput: $deletionInput) {
      id
      name
      deletedAt
    }
  }
`

const DELETION_REASONS: { value: DeletionReasonEnum; labelKey: string }[] = [
  { value: 'FOUND_BETTER_ALTERNATIVE', labelKey: 'Found a better alternative' },
  { value: 'NOT_USING_ANYMORE', labelKey: 'Not using anymore' },
  { value: 'SECURITY_AND_PRIVACY', labelKey: 'Security and privacy concerns' },
  { value: 'TECHNICAL_ISSUES', labelKey: 'Technical or performance issues' },
  { value: 'NEGATIVE_EXPERIENCE', labelKey: 'Negative interactions or harassment' },
  { value: 'PERSONAL_REASONS', labelKey: 'Personal reasons' },
  { value: 'OTHER', labelKey: 'Other' },
]

const STEP_STAY = 0
const STEP_CONFIRM_ID = 1
const STEP_REASON = 2
const COPY_FEEDBACK_MS = 1500
const MIN_CUSTOM_REASON_LENGTH = 3

/** Matches `ConfirmDialog` variant `delete` media styling */
const deleteMediaClassName =
  'bg-destructive/10 text-destructive dark:bg-destructive/80 dark:text-destructive-foreground/80'

const stayMediaClassName = 'bg-primary/10 text-primary dark:bg-primary/80 dark:text-primary-foreground/80'

type AccountDeleteModalProps = {
  open?: boolean
  onOpenChange?: (details: { open: boolean }) => void
  profileId?: string
}

const AccountDeleteModal = ({ profileId, open, onOpenChange }: AccountDeleteModalProps) => {
  const { t } = useTranslation()
  const instanceId = useId()
  const confirmInputRef = useRef<HTMLInputElement>(null)
  const [showSpinner, setShowSpinnerValue] = useState(false)
  const [accountId, setAccountId] = useState('')
  const [step, setStep] = useState(STEP_STAY)
  const [selectedReason, setSelectedReason] = useState('')
  const [customReason, setCustomReason] = useState('')
  const [copied, setCopied] = useState(false)
  const { logOut } = useAuth()
  const navigate = useNavigate()

  const [deleteProfile] = useMutation(DELETE_PROFILE, {
    onCompleted: () => {
      logOut.mutate()
      navigate({ to: '/' })
      setShowSpinnerValue(false)
      onOpenChange?.({ open: false })
    },
    onError: (error) => {
      toast.error(error.message)
      setShowSpinnerValue(false)
      onOpenChange?.({ open: false })
    },
  })

  const resetForm = () => {
    setAccountId('')
    setStep(STEP_STAY)
    setSelectedReason('')
    setCustomReason('')
    setCopied(false)
  }

  const onSubmit = () => {
    if (step === STEP_STAY) {
      setStep(STEP_CONFIRM_ID)
    } else if (step === STEP_CONFIRM_ID) {
      if (accountId === profileId) {
        setStep(STEP_REASON)
      }
    } else if (step === STEP_REASON) {
      if (selectedReason && customReason.trim().length >= MIN_CUSTOM_REASON_LENGTH) {
        setShowSpinnerValue(true)
        deleteProfile({
          variables: {
            id: profileId,
            deletionInput: {
              reason: selectedReason as DeletionReasonEnum,
              customReason: customReason.trim(),
            },
          },
        })
        setAccountId('')
        setSelectedReason('')
        setCustomReason('')
      }
    }
  }

  const handleOnClose = () => {
    resetForm()
    onOpenChange?.({ open: false })
  }

  const handleAccountIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAccountId(e.target.value)
  }

  const handleReasonChange = (value: string) => {
    setSelectedReason(value)
    if (value !== 'OTHER') {
      setCustomReason('')
    }
  }

  const handleCustomReasonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCustomReason(e.target.value)
  }

  const handleCopyAccountId = async () => {
    if (!profileId) {
      return
    }

    await navigator.clipboard.writeText(profileId)
    setCopied(true)
    window.setTimeout(() => {
      setCopied(false)
    }, COPY_FEEDBACK_MS)
  }

  useEffect(() => {
    setAccountId('')
    setStep(STEP_STAY)
    setSelectedReason('')
    setCustomReason('')
    setCopied(false)
  }, [open])

  useEffect(() => {
    if (open && step === STEP_CONFIRM_ID) {
      confirmInputRef.current?.focus()
    }
  }, [open, step])

  const canProceedConfirmId = accountId === profileId
  const canProceedReason = Boolean(selectedReason) && customReason.trim().length >= MIN_CUSTOM_REASON_LENGTH

  const dialogTitle =
    step === STEP_STAY
      ? t('We would love for you to stay')
      : step === STEP_REASON
        ? t("Please tell us why you're deleting your account")
        : t('Delete Account')

  return (
    <AlertDialog
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          handleOnClose()
        }
      }}
      open={open}
    >
      <AlertDialogContent size="default">
        <form
          className="contents"
          onSubmit={(event) => {
            event.preventDefault()
            onSubmit()
          }}
        >
          <AlertDialogHeader>
            <AlertDialogMedia className={step === STEP_STAY ? stayMediaClassName : deleteMediaClassName}>
              {step === STEP_STAY ? <HeartHandshakeIcon /> : <Trash2Icon />}
            </AlertDialogMedia>
            <AlertDialogTitle>{dialogTitle}</AlertDialogTitle>
            <AlertDialogDescription>
              {step === STEP_STAY ? (
                <>
                  <span className="block">
                    {t(
                      'It does not cost anything to remain a member. We would greatly value you continuing to be part of this community and helping the site grow.'
                    )}
                  </span>
                  <span className="mt-2 block">{t('If you still want to delete your account, you can continue.')}</span>
                </>
              ) : step === STEP_CONFIRM_ID ? (
                <>
                  <span className="block">
                    {t('Deleting your account will remove all media and profile information.')}
                  </span>
                  <span className="mt-2 block">
                    {t(
                      'Your data and media may reside on the server for up to two months before it is permanently deleted.'
                    )}
                  </span>
                </>
              ) : (
                t('This information helps us improve our service for other users.')
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {step === STEP_CONFIRM_ID ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <p className="font-semibold text-base">{t('Confirm account ID')}</p>
                <div className="flex items-center justify-between gap-2">
                  <p className="break-all font-semibold text-base text-destructive">{profileId ?? ''}</p>
                  <Button
                    aria-label={t('Copy account ID')}
                    className="shrink-0"
                    onClick={handleCopyAccountId}
                    size="icon"
                    type="button"
                    variant="outline"
                  >
                    {copied ? <CheckIcon className="size-4" /> : <CopyIcon className="size-4" />}
                  </Button>
                </div>
                <Label className="sr-only" htmlFor={`${instanceId}-confirm-id`}>
                  {t('Confirm account ID')}
                </Label>
                <Input
                  autoComplete="off"
                  className="w-full"
                  id={`${instanceId}-confirm-id`}
                  onChange={handleAccountIdChange}
                  placeholder={t('Type your account ID here to confirm')}
                  ref={confirmInputRef}
                  value={accountId}
                />
              </div>
            </div>
          ) : null}

          {step === STEP_REASON ? (
            <div className="flex w-full flex-col items-start gap-2">
              <RadioGroup className="gap-3 pt-2" onValueChange={handleReasonChange} value={selectedReason || undefined}>
                {DELETION_REASONS.map((reason) => {
                  const itemId = `${instanceId}-reason-${reason.value}`
                  return (
                    <div className="flex items-center gap-2" key={reason.value}>
                      <RadioGroupItem id={itemId} value={reason.value} />
                      <Label className="font-normal" htmlFor={itemId}>
                        {t(reason.labelKey)}
                      </Label>
                    </div>
                  )
                })}
              </RadioGroup>

              {!!selectedReason && (
                <div className="flex w-full flex-col items-start gap-2 pt-2">
                  <p className="font-medium text-sm">{t('Please help us improve our service:')}</p>
                  <Textarea
                    onChange={handleCustomReasonChange}
                    placeholder={t('Please provide some details to help us out...')}
                    rows={3}
                    value={customReason}
                  />
                </div>
              )}
            </div>
          ) : null}

          <AlertDialogFooter className="flex-row flex-wrap sm:justify-end">
            {step > STEP_STAY && (
              <Button onClick={() => setStep(step - 1)} type="button" variant="outline">
                {t('Back')}
              </Button>
            )}
            <AlertDialogCancel disabled={showSpinner} type="button">
              {t('Keep my account')}
            </AlertDialogCancel>
            {step === STEP_STAY && <AlertDialogAction type="submit">{t('Continue deleting')}</AlertDialogAction>}
            {step === STEP_CONFIRM_ID && canProceedConfirmId && (
              <AlertDialogAction type="submit">{t('Next')}</AlertDialogAction>
            )}
            {step === STEP_REASON && (
              <AlertDialogAction disabled={showSpinner || !canProceedReason} type="submit" variant="destructive">
                {showSpinner ? <Spinner className="mr-2 size-4" /> : null}
                {t('Delete Account')}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export default AccountDeleteModal
